import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Title } from "../../../../themes/Default/components/Title";
import { Main } from "../../../../themes/Default/components/Main";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { toast } from "react-toastify";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { groupProgressManagement } from "../../../../hooks/api/GroupProgressManagement/GroupProgressManagement";
import { studentsManagement } from "../../../../hooks/api/StudentsManagement";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const AddProgress = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { groupId } = useParams();
    const { studentId } = useParams();
    const { classNumber } = useParams();

    const {useProgressNoteListMutation, useViewGroupMutation, useAddProgressMutation, useProgressListMutation, useUpdateProgressMutation} = groupsManagement;
    const {useViewStudentsMutation} = studentsManagement;
    const [viewStudent] = useViewStudentsMutation();
    const [studentName, setStudentName] = useState("");

    const [hasTests, setHasTests] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [viewGroup] = useViewGroupMutation();
    const [addProgress] = useAddProgressMutation();
    const [editProgress] = useUpdateProgressMutation();
    const [listProgress] = useProgressListMutation();
    const [columns, setColumns] = useState();
    const [progress, setProgress] = useState();

    const { useGroupProgressByGroupTypeMutation, useGroupProgressTypeOptionByGroupTypeMutation } = groupProgressManagement;
    const [groupProgress] = useGroupProgressByGroupTypeMutation();
    const [groupProgressTypeOptions] = useGroupProgressTypeOptionByGroupTypeMutation();

    const [groupTypeId, setGroupTypeId] = useState("");
    const [notesList] = useProgressNoteListMutation();
    const [notesCounts, setNotesCounts] = useState({});

    const [studentGroups, setStudentGroups] = useState([]);

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        { title: t('groups.progress'),isActive: true },
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []),
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const gotoGroupProgressNote = (groupProgressId) => {
        navigate("/groups/" + groupId + "/progress/" + groupProgressId + "/notes");
    }

    const onSaved = (values) => {
        const body = {...values};
        addProgress({body, groupId}).then((result) => {
            if (result?.error) {
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadProgress();
            }
        });
    };

    const onEdit = (values, groupProgressId) => {
        const body = {...values};
        editProgress({body, groupProgressId}).then((result) => {
            if (result?.error) {
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadProgress();
            }
        });
    };

    const loadProgress = () => {
        listProgress({groupId, classNumber}).then((result) => {
            setProgress(result.data);
        });
    };

    useEffect(() => {
        viewStudent(studentId).then((result) => {
            setStudentName(result?.data?.firstName + " " + result?.data?.lastName);
            setStudentGroups(result?.data?.groups || []);
        });
        viewGroup(groupId).then((result) => {
            setGroupTypeId(result?.data?.type);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasPlan(result?.data?.hasPlan);
            groupProgress({ groupTypeId: result?.data?.type}).then((result1) => {
                setColumns(result1?.data?.records);
            });
        });
        loadProgress();
    }, [groupId, classNumber]);

    const [valuesByColumn, setValuesByColumn] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            const newValues = {};
            for (const date of columns) {
                const result = await groupProgressTypeOptions({
                    settingsGroupProgressId: date.id,
                    groupTypeId: groupTypeId,
                });
                newValues[date.id] = result?.data?.records || [];
            }
            setValuesByColumn(newValues);
        };

        if (columns?.length > 0) {
            setValuesByColumn({}); 
            fetchData();
        } else {
            setValuesByColumn({});
        }
    }, [columns, groupTypeId]);

    useEffect(() => {
        if (!progress || progress.length === 0) return;
    
        progress.forEach(item => {
            item.options?.forEach(option => {
                const groupProgressId = option.groupProgressId;
                if (!notesCounts[groupProgressId]) {
                    notesList({groupProgressId}).then(result => {
                        setNotesCounts(prev => ({
                            ...prev,
                            [groupProgressId]: result?.data?.totalRecords || 0
                        }));
                    });
                }
            });
        });
    }, [progress]);

    return (
        <DefaultLayout>
            
        <TopNavigation>
            <ButtonBack to={'/groups/' + groupId + "/progress"} />
        </TopNavigation>
        <Tabs tabs={tabs} />
        <Main>
            <Title fullWidth={"100%"}>
                {studentName}
                <span className="numOfClasses">
                    Trenutno popunjavate napredak na kraju {classNumber + "."} {"časa"}
                </span>
            </Title>
            <Breadcrumps>
                <Link title={t("groups.title")} icon="fa-user-group" />
                <Link title={t("groups.progress")} isActive={true} />
            </Breadcrumps>
                    {columns?.length > 0 &&
                    columns.map((column, index) => {
                        const getSelectedOptionId = () => {
                            if (!progress || progress.length === 0) return '';
                        
                            const progressItem = progress.find(group => 
                                group.settingsGroupProgressId === column.id &&
                                Array.isArray(group.options) &&
                                group.options.some(option => option.student === Number(studentId)) 
                            );
                        
                            if (progressItem && progressItem.options) {
                                const selectedOption = progressItem.options.find(option => option.student === Number(studentId));
                                return selectedOption ? selectedOption.option : '';
                            }
                        
                            return '';
                        };

                        const getGroupProgressId = () => {
                            if (!progress || progress.length === 0) return '';
                        
                            const progressItem = progress.find(group => 
                                group.settingsGroupProgressId === column.id &&
                                Array.isArray(group.options) &&
                                group.options.some(option => option.student === Number(studentId)) 
                            );
                        
                            if (progressItem && progressItem.options) {
                                const selectedOption = progressItem.options.find(option => option.student === Number(studentId));
                                return selectedOption ? selectedOption.groupProgressId : '';
                            }
                        
                            return '';
                        };

                        const currentGroup = studentGroups.find(g => g.id === Number(groupId));
                        const isArchived = currentGroup?.studentStatus || false;

                        return (
                            <Row>
                                <Field >
                                   <Select
                                        label={column.name}
                                        onChange={(e) => {
                                            if (getSelectedOptionId()) {
                                                const body = {
                                                    groupProgressTypeOptionId: e.target.value,
                                                };
                                                onEdit(body, getGroupProgressId());
                                            } else {
                                                const body = {
                                                    studentId: studentId,
                                                    classNumber: classNumber,
                                                    groupProgressTypeOptionId: e.target.value,
                                                };
                                                onSaved(body);
                                            }
                                        }}
                                        disabled={isArchived}
                                        selectedValue={getSelectedOptionId()}
                                        options={valuesByColumn[column.id] || []}
                                        searchVisible={false}
                                    />
                                </Field>
                                <Button
                                    badgeNumber={notesCounts[getGroupProgressId()]}
                                    icon="fa-regular fa-comment-dots"
                                    onClick={() => {
                                        const currentSelectedValue = getGroupProgressId();

                                        if (currentSelectedValue) {
                                            gotoGroupProgressNote(currentSelectedValue);
                                        }
                                    }}
                                />
                            </Row>
                        );
                    })}
            </Main>
    </DefaultLayout>
    );
};

export default AddProgress;
